import type { RepositoryInterface } from '@/service/repository';
import Repository from '@/service/repository';
import type {
  AuthIdentifier,
  ClientInterface,
  FirebaseStorage,
  Firestore,
  Groups,
} from '@/service/types';
import type { UploadClientInterface, UploadedFile } from '@/service/upload';
import UploadClient from '@/service/upload';
import type { Recipe, RecipePayload } from '@/service/recipe/types';
import { RecipeSchema } from '@/service/recipe/types';
import { imagesOf } from '@/service/recipe/metadata';
import { v4 } from 'uuid';
import type {
  DocumentData,
  QueryDocumentSnapshot,
  SnapshotOptions,
} from 'firebase/firestore';
import { collection, query, where } from 'firebase/firestore';
import {
  type _RefFirestore,
  useCollection,
  useDocument,
  useFirebaseStorage,
  useFirestore,
} from 'vuefire';
import { computed, type Ref } from 'vue';
import { httpsCallable } from 'firebase/functions';
import { firebase } from '@/config/Firebase';
import { readRecipe } from '@/service/recipe/readRecipe';

const converter = {
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions,
  ): Recipe {
    return readRecipe(snapshot.id, snapshot.data(options));
  },
  toFirestore(recipe: Recipe): DocumentData {
    return recipe;
  },
};

export default class RecipeClient implements ClientInterface<
  Recipe,
  RecipePayload
> {
  private readonly recipes: RepositoryInterface<Recipe>;
  private readonly uploadClient: UploadClientInterface;
  private readonly firestore: Firestore;
  private readonly identifier: AuthIdentifier;

  constructor(
    identifier: AuthIdentifier,
    store: Firestore,
    storage: FirebaseStorage,
  ) {
    this.identifier = identifier;
    this.firestore = store;
    this.recipes = new Repository<Recipe>('recipes', store, identifier);
    this.uploadClient = new UploadClient(identifier, storage);
  }

  static async setup(identifier: AuthIdentifier): Promise<RecipeClient> {
    const firestore = useFirestore();
    const firebaseStorage = useFirebaseStorage();
    const groups = new Repository<Groups>('groups', firestore, identifier);
    const { users } = await groups.getOne(identifier.uid);
    return new RecipeClient(
      { ...identifier, groups: users },
      firestore,
      firebaseStorage,
    );
  }

  collectionRef(): Ref<Recipe[]> {
    const ownRecipes = useCollection(
      this.recipes.collectionRef().withConverter(converter),
      { ssrKey: 'recipe' },
    );
    const sharedQuery = query(
      collection(this.firestore, 'recipes'),
      where('sharedWith', 'array-contains', this.identifier.uid),
    ).withConverter(converter);
    const sharedRecipes = useCollection(sharedQuery, {
      ssrKey: 'recipe-shared',
    });

    return computed(() => {
      const map = new Map<string, Recipe>();
      for (const r of ownRecipes.value ?? []) map.set(r.id, r);
      for (const r of sharedRecipes.value ?? []) {
        if (!map.has(r.id)) map.set(r.id, r);
      }
      return Array.from(map.values());
    });
  }

  canEdit(recipe: Recipe): boolean {
    return this.identifier.groups?.includes(recipe.user ?? '') ?? false;
  }

  documentRef(id: string): _RefFirestore<Recipe | undefined> {
    return useDocument(this.recipes.documentRef(id).withConverter(converter));
  }

  async get(): Promise<Recipe[]> {
    return (await this.recipes.get()).map((recipe) =>
      readRecipe(recipe.id, recipe as unknown as Record<string, unknown>),
    );
  }

  async getOne(id: string): Promise<Recipe> {
    return readRecipe(
      id,
      (await this.recipes.getOne(id)) as unknown as Record<string, unknown>,
    );
  }

  async save(payload: RecipePayload): Promise<void> {
    await this.recipes.save({
      ...payload.recipe,
      ...(await this.uploadImages(payload)),
      createdAt: payload.recipe.createdAt ?? Date.now(),
    });
  }

  async update(payload: RecipePayload): Promise<void> {
    await this.recipes.update({
      ...payload.recipe,
      ...(await this.uploadImages(payload)),
    });
  }

  async remove(id: string): Promise<void> {
    await this.recipes.remove(id);
  }
  async importFromUrl(url: string): Promise<Recipe> {
    const importFn = httpsCallable(firebase.functions, 'importRecipeFromUrl');
    const result = await importFn({ url });
    return RecipeSchema.parse({
      ...(result.data as object),
      sourceUrl: url,
    });
  }

  async importFromImage(file: File): Promise<Recipe> {
    const base64 = await this.fileToBase64(file);
    const importFn = httpsCallable(firebase.functions, 'importRecipeFromImage');
    const result = await importFn({ image: base64, mimeType: file.type });
    return RecipeSchema.parse(result.data);
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        resolve(dataUrl.split(',')[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // The cover (a new one replaces the old) followed by the other pictures:
  // the ones kept in the form (images[0] is the old cover) plus new photos
  private async uploadImages(
    payload: RecipePayload,
  ): Promise<Pick<Recipe, 'image' | 'images'>> {
    const { path: image } = await this.uploadImage(payload);
    const others = imagesOf(payload.recipe).slice(1);
    const uploaded = await Promise.all(
      (payload.photos ?? []).map((photo) =>
        this.uploadClient.upload({
          path: `recipes/${payload.recipe.id}-${v4()}`,
          data: photo,
          extension: 'png',
        }),
      ),
    );
    const images = [
      image,
      ...others,
      ...uploaded.map(({ path }) => path),
    ].filter(Boolean);
    // Without a cover, the first picture becomes it, so image is always
    // images[0]
    return { image: images[0] ?? '', images };
  }

  private async uploadImage(payload: RecipePayload): Promise<UploadedFile> {
    if (payload.images.length) {
      return await this.uploadClient.upload({
        path: `recipes/${payload.recipe.id}`,
        data: payload.images[0],
        extension: 'png',
      });
    }
    return { path: payload.recipe.image };
  }
}
